/**
 * Workspace-proxy compatibility shim.
 *
 * In legacy `.local` mode, every portal serves at root (`/`) and fetches go to
 * `/api/*`, intercepted by Vite's proxy. In workspace-proxy mode (set by
 * Seedarator/CLI via `FSEED_BASE_PATH`), the portal serves at a sub-path like
 * `/console/`, but client code still calls `fetch('/api/seeds')` — which the
 * workspace proxy can't route (it doesn't know which app's API).
 *
 * This patch transparently prefixes `/api/*` fetches with `import.meta.env.BASE_URL`
 * so the URL becomes `/console/api/seeds` and the workspace proxy can route it.
 * No-op when `BASE_URL` is `/` (today's `pnpm dev`).
 *
 * Side-effect import: just `import './lib/fetch-base-url'` from main.tsx.
 */
const apiBase = import.meta.env.BASE_URL.replace(/\/$/, '')

if (apiBase) {
  const origFetch = window.fetch
  window.fetch = function patched(input, init): Promise<Response> {
    if (typeof input === 'string' && input.startsWith('/api')) {
      return origFetch.call(this, apiBase + input, init)
    }
    if (input instanceof URL && input.pathname.startsWith('/api')) {
      const u = new URL(input.toString())
      u.pathname = apiBase + u.pathname
      return origFetch.call(this, u, init)
    }
    if (input instanceof Request) {
      const u = new URL(input.url, window.location.origin)
      if (u.pathname.startsWith('/api')) {
        u.pathname = apiBase + u.pathname
        return origFetch.call(this, new Request(u, input), init)
      }
    }
    return origFetch.call(this, input, init)
  }
}
